import { useMemo, useCallback, useEffect, useState } from "react";
import { normalizeValue } from "./utils";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorFooter,
  EntitySelectorFooterAction,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorStepHeader,
  useEntitySelector,
  useEntitySelectorOpen,
  type EntitySelectorConfig,
  type EntitySelectorContentProps,
  type EntitySelectorVariant,
  type EntitySelection,
} from "@salestrak/ui";
import { useCustomFieldPool } from "./hooks";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";
import type { CustomFieldType } from "#app/lib/data";
import {
  FIELD_TYPE_POOL_ID,
  useFieldTypeSelectorConfig,
} from "#app/lib/hooks/use-field-type-order";

export type CustomFieldSelectorProps = Omittable<{
  value?: string | string[] | null;
  onValueChange?: (ids: string[]) => void;
  /** Custom field IDs to exclude (e.g., already-assigned fields) */
  exclude?: string[];
  maxSelections?: number;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  placeholder?: string;
  size?: SelectorSize;
  disabled?: boolean;
  /** Controlled open, for a host that closes the picker itself (a footer
   *  action that hands off to a dialog). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * The picker can mint a field: its footer offers "Create Field", which
   * drills into a list of the field TYPES in the same popup, and picking one
   * closes the picker and reports the kind here — the host opens the create
   * form on it. Omitted, the picker only attaches fields that exist.
   */
  onCreateType?: (type: CustomFieldType) => void;
  children?: React.ReactNode;
}>;

export function CustomFieldSelector({
  value,
  onValueChange,
  exclude,
  maxSelections,
  variant,
  placeholder = "Select fields...",
  size,
  disabled,
  open: openProp,
  onOpenChange,
  onCreateType,
  children,
}: CustomFieldSelectorProps) {
  const { pool } = useCustomFieldPool({ exclude });
  const typeConfig = useFieldTypeSelectorConfig();

  // Open state is resolved here whether or not the host controls it: a type
  // pick closes the picker from inside `onBeforeSelect`, which has no other
  // way to the root's flag.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      // The type list is a second pool, never a tab: it is reached through
      // the footer verb and shown alone (see CustomFieldSelectorContent).
      pools: onCreateType ? [pool, ...typeConfig.pools] : [pool],
      defaultPool: pool.id,
      maxSelections,
      searchPlaceholder: "Search fields...",
      emptyMessage: "No fields found",
      noItemsMessage: "No custom fields yet",
    }),
    [pool, typeConfig.pools, maxSelections, onCreateType],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const ids = normalizeValue(value);
    if (ids.length === 0) return [];
    const itemMap = new Map(pool.items.map((item) => [item.id, item]));
    return ids
      .map((id) => {
        const entity = itemMap.get(id);
        if (!entity) return null;
        return { id, poolId: pool.id, entity };
      })
      .filter(Boolean) as EntitySelection[];
  }, [value, pool]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      onValueChange?.(next.map((s) => s.id));
    },
    [onValueChange],
  );

  // A type row acts instead of selecting: the pick is the answer to "Create
  // Field", so the picker closes and the host takes over.
  const handleBeforeSelect = useCallback(
    (entity: { id: string }, poolId: string) => {
      if (poolId !== FIELD_TYPE_POOL_ID) return true;
      setOpen(false);
      onCreateType?.(entity.id as CustomFieldType);
      return false;
    },
    [onCreateType, setOpen],
  );

  return (
    <EntitySelector
      title="Fields"
      variant={variant}
      config={config}
      value={selections}
      onValueChange={handleValueChange}
      open={open}
      onOpenChange={setOpen}
      onBeforeSelect={onCreateType ? handleBeforeSelect : undefined}
    >
      {children ?? (
        <>
          <EntitySelectorTrigger
            placeholder={placeholder}
            size={toEntityTriggerSize(size)}
            disabled={disabled}
          />
          {onCreateType ? (
            <CustomFieldSelectorContent />
          ) : (
            <EntitySelectorContent>
              <EntitySelectorInput />
              <EntitySelectorPool value={pool.id} />
            </EntitySelectorContent>
          )}
        </>
      )}
    </EntitySelector>
  );
}

export type CustomFieldSelectorContentProps = Pick<
  EntitySelectorContentProps,
  "align" | "side"
>;

/**
 * The picker's two steps in one popup. The list step — search, the fields
 * that exist, "Create Field" in the footer — is where it opens. The verb
 * needs one more answer, WHICH kind, so it swaps the list for the type step:
 * a title row with the way back, the same search now over the types, the
 * types in their curated order. No tabs (the types are not a second place to
 * look for a field) and no second surface (a popup over a popup, or a sheet
 * over a sheet on a phone, for one pick). Picking a type closes the picker
 * — the create form takes over. Closing the picker on either step reopens
 * it on the list.
 */
export function CustomFieldSelectorContent({
  align,
  side,
}: CustomFieldSelectorContentProps) {
  const { config, activePool, setActivePool, setSearch, getPool } =
    useEntitySelector();
  const open = useEntitySelectorOpen();
  const fieldsPoolId = config.pools[0]?.id ?? "";
  const canCreate = getPool(FIELD_TYPE_POOL_ID) != null;
  const creating = canCreate && activePool === FIELD_TYPE_POOL_ID;

  const goTo = useCallback(
    (poolId: string) => {
      // A query typed on one step is not a query on the other.
      setSearch("");
      setActivePool(poolId);
    },
    [setActivePool, setSearch],
  );

  // The root keeps its active pool across a close; a reopen must land on the
  // list, not on the step the last visit ended on.
  useEffect(() => {
    if (!open && activePool !== fieldsPoolId) setActivePool(fieldsPoolId);
  }, [open, activePool, fieldsPoolId, setActivePool]);

  return (
    <EntitySelectorContent align={align} side={side}>
      {creating ? (
        <EntitySelectorStepHeader
          title="Create Field"
          onBack={() => goTo(fieldsPoolId)}
        />
      ) : null}
      <EntitySelectorInput
        placeholder={creating ? "Search field types..." : undefined}
      />
      {creating ? (
        // A type is picked, never checked: the pick closes the picker and
        // the create form takes over, so the rows carry no marks.
        <EntitySelectorPool
          value={FIELD_TYPE_POOL_ID}
          hideLabel
          hideSelectionMarks
        />
      ) : (
        <EntitySelectorPool value={fieldsPoolId} />
      )}
      {canCreate && !creating ? (
        <EntitySelectorFooter>
          <EntitySelectorFooterAction
            icon="plus"
            onClick={() => goTo(FIELD_TYPE_POOL_ID)}
          >
            Create Field
          </EntitySelectorFooterAction>
        </EntitySelectorFooter>
      ) : null}
    </EntitySelectorContent>
  );
}
